import React from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/sections/Reveal";
import ProofBar from "@/components/sections/ProofBar";
import DarkCTA from "@/components/sections/DarkCTA";
import NewsletterCapture from "@/components/sections/NewsletterCapture";
import { HOME_PAGE } from "@/constants/testIds";
import { useSiteImages } from "@/lib/useSiteImages";

const engagements = [
  {
    title: "Federal Agency — AI Readiness & Governance",
    body: "AI readiness and governance assessment. 90-day engagement. White House and Department of Energy.",
  },
  {
    title: "London Healthtech — Founding CTO",
    body: "Founding CTO engagement. Secured £6.2M Innovate UK grant. Infrastructure deployed across UK and West Africa.",
  },
  {
    title: "Fortune 500 Enterprise — Technology Strategy",
    body: "Technology strategy and product architecture. One of 400+ product launches across Copper & Vine's 20-year history.",
  },
  {
    title: "Howard University — Academic Technology",
    body: "Product and technology strategy. Ongoing engagement with a flagship HBCU on digital infrastructure.",
  },
];

export default function Home() {
  const IMAGES = useSiteImages();
  return (
    <div data-testid={HOME_PAGE.root}>
      {/* HERO — Dark editorial */}
      <section
        className="rk-section"
        style={{
          backgroundColor: "var(--rk-dark)",
          color: "var(--rk-cream)",
          paddingTop: "calc(var(--rk-section-py) + 60px)",
        }}
      >
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <Reveal>
                <div className="rk-eyebrow" style={{ color: "rgba(250,247,242,0.6)" }}>
                  Rakia Finley — Fractional CTO/CIO
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  className="rk-heading-hero mt-6"
                  style={{ color: "var(--rk-cream)" }}
                >
                  Twenty years of building things that hold.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="mt-8"
                  style={{
                    color: "rgba(250,247,242,0.85)",
                    fontSize: "clamp(18px, 1.4vw, 21px)",
                    lineHeight: 1.6,
                    maxWidth: 620,
                  }}
                >
                  Rakia Finley is a fractional CTO/CIO, executive technologist,
                  and founder who has launched over 400 products across Fortune
                  500 enterprises, federal agencies, and research institutions.
                  She builds infrastructure. She does not merely advise on it.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/about"
                    data-testid={HOME_PAGE.heroGhostCta}
                    className="rk-btn-outline rk-btn-outline-on-dark"
                  >
                    Explore the work
                  </Link>
                  <Link
                    to="/advisory"
                    data-testid={HOME_PAGE.heroPrimaryCta}
                    className="rk-btn-primary rk-btn-primary-on-dark"
                  >
                    Work with Rakia
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <Reveal delay={0.08} y={28}>
                <div
                  style={{
                    position: "relative",
                    aspectRatio: "4/5",
                    overflow: "hidden",
                    borderRadius: 2,
                    border: "1px solid rgba(181,114,42,0.4)",
                  }}
                >
                  <img
                    src={IMAGES.heroPortrait}
                    alt="Rakia Finley portrait — fractional CTO/CIO"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: "saturate(0.96) contrast(1.04)",
                    }}
                    loading="eager"
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(22,24,27,0) 60%, rgba(22,24,27,0.35) 100%)",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <ProofBar />

      {/* WHAT I DO */}
      <section className="rk-section">
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="rk-eyebrow">Engagements</div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="rk-heading-section mt-5">
                  The right engagement for where you are.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
                <Reveal>
                  <Link
                    to="/advisory"
                    data-testid={HOME_PAGE.whatIdoCardCto}
                    className="rk-card block group"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <h3
                        style={{
                          fontFamily: "var(--font-heading)",
                          fontSize: "clamp(26px, 2.6vw, 36px)",
                          lineHeight: 1.1,
                        }}
                      >
                        Fractional CTO / CIO
                      </h3>
                      <span
                        className="rk-caption"
                        style={{ color: "var(--rk-copper)" }}
                      >
                        See Advisory →
                      </span>
                    </div>
                    <div className="rk-rule my-5" />
                    <p className="rk-body" style={{ color: "var(--rk-muted)" }}>
                      For founders and operators who need senior technology
                      judgment — and someone who will execute, not just advise.
                    </p>
                  </Link>
                </Reveal>
                <Reveal delay={0.05}>
                  <Link
                    to="/speaking"
                    data-testid={HOME_PAGE.whatIdoCardSpeaking}
                    className="rk-card block"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <h3
                        style={{
                          fontFamily: "var(--font-heading)",
                          fontSize: "clamp(26px, 2.6vw, 36px)",
                          lineHeight: 1.1,
                        }}
                      >
                        Speaking
                      </h3>
                      <span
                        className="rk-caption"
                        style={{ color: "var(--rk-copper)" }}
                      >
                        See Speaking →
                      </span>
                    </div>
                    <div className="rk-rule my-5" />
                    <p className="rk-body" style={{ color: "var(--rk-muted)" }}>
                      For conferences, executive events, and panels on AI
                      governance, fractional leadership, and the future of
                      enterprise technology.
                    </p>
                  </Link>
                </Reveal>
                <Reveal delay={0.1}>
                  <Link
                    to="/advisory"
                    data-testid={HOME_PAGE.whatIdoCardCircle}
                    className="rk-card block"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <h3
                        style={{
                          fontFamily: "var(--font-heading)",
                          fontSize: "clamp(26px, 2.6vw, 36px)",
                          lineHeight: 1.1,
                        }}
                      >
                        The Advisory Circle
                      </h3>
                      <span
                        className="rk-caption"
                        style={{ color: "var(--rk-copper)" }}
                      >
                        Apply →
                      </span>
                    </div>
                    <div className="rk-rule my-5" />
                    <p className="rk-body" style={{ color: "var(--rk-muted)" }}>
                      A private room for senior leaders navigating high-stakes
                      technology decisions. Limited membership. Application
                      required.
                    </p>
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SELECTED ENGAGEMENTS */}
      <section
        className="rk-section"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Work</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Selected engagements.</h2>
          </Reveal>
          <div className="mt-12">
            {engagements.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.04}>
                <article
                  className="py-8 grid grid-cols-1 md:grid-cols-12 gap-6"
                  style={{
                    borderTop: "1px solid var(--rk-border)",
                    borderBottom:
                      i === engagements.length - 1
                        ? "1px solid var(--rk-border)"
                        : "none",
                  }}
                >
                  <div className="md:col-span-5">
                    <h3
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "clamp(22px, 2.2vw, 28px)",
                        lineHeight: 1.15,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {e.title}
                    </h3>
                  </div>
                  <div className="md:col-span-7">
                    <p className="rk-body" style={{ color: "var(--rk-ink)" }}>
                      {e.body}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.08}>
            <p className="mt-8 rk-caption">
              Client names shared under NDA upon engagement inquiry.
            </p>
          </Reveal>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="rk-section">
        <NewsletterCapture source="home" />
      </section>

      <DarkCTA
        eyebrow="Engage"
        heading="If you are building something serious."
        body="Rakia takes engagements personally and selectively. If you have a high-stakes technology decision ahead — a build, a raise, a governance gap, or a leadership transition — this is where you start."
        ctaLabel="Work with Rakia"
        ctaTo="/advisory"
      />
    </div>
  );
}
